// Score normalization — the model-side twin of the store's chart `normalize`.
//
// Once the store's truth flips to Score (slice 4), every mutation funnels
// through ONE commit helper which calls this, so neither edit direction
// (form/harmony edits from the Workbench, content edits from the
// Score page/commands) can leave the model denormalized. Invariants:
//   1. at least one measure exists
//   2. measure 1 carries a time signature
//   3. the last measure closes 'final'; a paired-coda boundary may also retain
//      its intentional interior final
//   4. every part has exactly one PartMeasure per FormMeasure
//   5. an underfull first bar with real missing time is a pickup (same test
//      the MusicXML importer uses) — already-imported scores such as Nica's
//      Dream shipped before that detection and would otherwise play a
//      two-beat lead-in as a full bar, with the band on the wrong "one"
//   6. the reharm layer state agrees with the form's harmony (reharm.ts)
//   7. every spanner's endpoints exist — the edit ops prune their own, and
//      this repairs a score persisted before one of them did (a measure
//      delete once left its slurs behind), which validateScore would call an
//      error and the saved-tune guard would drop whole
// Returns a new Score; never mutates its input.

import { fractionToNumber, measureQuarters } from './duration';
import { voiceLength } from './timing';
import { reconcileReharm } from './reharm';
import type { FormMeasure, Part, PartMeasure, Score, Spanner, VoiceItem } from './types';

/**
 * A bare iReal coda pair uses the first sign as "leave the regular chorus here"
 * and the second as the detached final-pass target. A final barline at or after
 * the first sign and before its target is semantic, not stray formatting.
 */
export function isPairedCodaFinal(
  measures: readonly { coda?: boolean }[],
  measureIndex: number,
): boolean {
  const codas = measures.flatMap((measure, index) => (measure.coda ? [index] : []));
  for (let i = 0; i + 1 < codas.length; i += 2) {
    if (measureIndex >= codas[i] && measureIndex < codas[i + 1]) return true;
  }
  return false;
}

export function normalizeScore(score: Score): Score {
  const form: FormMeasure[] = score.form.map((m) => ({ ...m }));

  if (form.length === 0) {
    form.push({ id: `m:seed`, harmony: [], timeSig: [4, 4] });
  }
  if (!form[0].timeSig) form[0].timeSig = [4, 4];

  const last = form[form.length - 1];
  if (!last.close) last.close = 'final';
  form.forEach((m, i) => {
    if (i < form.length - 1 && m.close === 'final' && !isPairedCodaFinal(form, i)) {
      m.close = 'single';
    }
  });

  const parts: Part[] = score.parts.map((p) => ({
    ...p,
    measures: alignMeasures(p, form.length),
  }));

  inferLeadingPickup(form[0], parts);

  const spanners = liveSpanners(score.spanners, parts);

  // 6. reharm layers agree with the form (the form wins) — see reharm.ts
  return reconcileReharm(
    spanners === score.spanners ? { ...score, form, parts } : { ...score, form, parts, spanners },
  );
}

/** The spanners whose endpoints both exist; the same array when all do. */
function liveSpanners(
  spanners: Spanner[] | undefined,
  parts: readonly Part[],
): Spanner[] | undefined {
  if (!spanners?.length) return spanners;
  const ids = new Set<string>();
  const collect = (items: readonly VoiceItem[]) => {
    for (const item of items) {
      if (item.kind === 'tuplet') collect(item.items);
      else ids.add(item.id);
    }
  };
  for (const part of parts) {
    for (const measure of part.measures) for (const voice of measure.voices) collect(voice.items);
  }
  const live = spanners.filter((s) => ids.has(s.fromEventId) && ids.has(s.toEventId));
  return live.length === spanners.length ? spanners : live;
}

/**
 * A first bar whose written time falls short of the meter, with no authored
 * rest filling the gap, can only be an anacrusis. Harmony that already
 * claims the full bar wins — that is a short melody over a real downbeat,
 * not a pickup. Empty content stays empty (a rest intro, not a partial).
 */
function inferLeadingPickup(measure: FormMeasure, parts: readonly Part[]): void {
  if (measure.pickupBeats != null) return;
  const meter = measure.timeSig;
  if (!meter) return;

  const capacityQ = fractionToNumber(measureQuarters(meter));
  const contentQ = leadingContentQuarters(parts);
  const harmonyBeats = measure.harmony.reduce((sum, chord) => sum + chord.beats, 0);
  if (contentQ <= 1e-9) return;
  if (contentQ >= capacityQ - 1e-9) return;
  if (harmonyBeats >= meter[0] - 1e-9) return;

  measure.pickupBeats = contentQ * (meter[1] / 4);
}

function leadingContentQuarters(parts: readonly Part[]): number {
  let furthest = 0;
  for (const part of parts) {
    const first = part.measures[0];
    if (!first) continue;
    for (const voice of first.voices) {
      const length = fractionToNumber(voiceLength(voice));
      if (length > furthest) furthest = length;
    }
  }
  return furthest;
}

/** Pad (empty bars) or truncate a part's measures to the form's length. */
function alignMeasures(part: Part, formLength: number): PartMeasure[] {
  if (part.measures.length === formLength) return part.measures.map((pm) => ({ ...pm }));
  const out = part.measures.slice(0, formLength).map((pm) => ({ ...pm }));
  while (out.length < formLength) {
    out.push({ id: `pm:pad:${part.id}:${out.length}`, voices: [] });
  }
  return out;
}
